import { ParameterType } from '@/modules/common/enums/parameter-type.ts';
import DocumentMasterApi from '@/modules/document/api/document-master-api.ts';
import type { Schema } from '@/modules/ui/models/schema/schema.ts';
import { SchemaInput } from '@/modules/ui/models/schema/schema-input.ts';
import { SchemaCombobox } from '@/modules/ui/models/schema/schema-combobox.ts';
import { SchemaPrompt } from '@/modules/ui/models/schema/schema-prompt.ts';
import { SchemaInputNumber } from '@/modules/ui/models/schema/schema-input-number.ts';
import { SchemaDate } from '@/modules/ui/models/schema/schema-date.ts';
import type { SchemaControlIndent } from '@/modules/common/models/schema/schema-control.ts';
import { calcRows } from '@/modules/common/utils/field-rows.ts';
import type { DocumentTypeParameterDto } from '@/modules/document/dto/document-type-parameter.dto.ts';
import { getInputMaskMaxLength, toInputMask } from '@/modules/common/utils/to-input-mask.ts';

export type ParameterFormFieldOptions = {
  titleAsLabel?: boolean;
  autoGrowTextarea?: boolean;
};

const EDIT_TEXTAREA_ROWS = 3;

export class Parameter {
  readonly type: ParameterType;
  readonly title: string;
  readonly number: number;
  readonly maxLength: number;
  readonly isList: boolean;
  readonly required: boolean;
  readonly mask: string;
  readonly canUseAi: boolean;

  constructor(response: DocumentTypeParameterDto) {
    this.mask = response.Маска ?? '';
    this.type = this.getType(response.Тип);
    this.title = response.Название;
    this.number = response.Номер;
    this.maxLength = response.Длина;
    this.isList = response.Справочник === 1;
    this.required = response.Обязательный === 1;
    this.canUseAi = response.CAN_USE_AI === 1;
  }

  get isTextarea() {
    return this.maxLength > 25;
  }

  get inputMask() {
    return this.isTextarea ? undefined : toInputMask(this.mask);
  }

  getFormField(
    documentTypeId: number,
    indent: SchemaControlIndent = 'default',
    { titleAsLabel = false, autoGrowTextarea = false }: ParameterFormFieldOptions = {}
  ): Schema {
    const common = {
      name: `parameters.${this.number}`,
      required: this.required,
      placeholder: this.title,
      label: titleAsLabel ? this.title : undefined,
      indent,
    };

    const min = this.mask.includes('#') ? undefined : 0;

    switch (this.type) {
      case ParameterType.Date:
        return new SchemaDate(common);
      case ParameterType.Integer:
      case ParameterType.Decimal:
        return new SchemaInputNumber({
          ...common,
          maxLength: this.type === ParameterType.Integer ? this.maxLength : undefined,
          min,
          decimal: this.type === ParameterType.Decimal,
        });
      case ParameterType.Time:
        return new SchemaInput({ ...common, type: 'time' });
      case ParameterType.String: {
        if (this.isList) {
          return new SchemaCombobox({
            ...common,
            options: (signal: AbortSignal) =>
              DocumentMasterApi.getParameterOptions(documentTypeId, this.number, signal),
            expanded: true,
          });
        }

        const { isTextarea, inputMask } = this;
        const textareaRows = isTextarea ? calcRows(this.maxLength) : undefined;
        const shouldAutoGrow = autoGrowTextarea && (textareaRows ?? 0) > EDIT_TEXTAREA_ROWS;

        return new SchemaPrompt({
          ...common,
          field: isTextarea ? 'textarea' : 'input',
          getHints: (signal: AbortSignal, query?: string) =>
            DocumentMasterApi.getHints(documentTypeId, this.number, query, signal),
          maxLength: isTextarea ? this.maxLength : getInputMaskMaxLength(inputMask, this.maxLength),
          noResize: !shouldAutoGrow,
          slot: this.canUseAi ? 'ai-button' : undefined,
          rows: shouldAutoGrow ? EDIT_TEXTAREA_ROWS : textareaRows,
          autoGrow: shouldAutoGrow,
          autoGrowMaxRows: shouldAutoGrow ? textareaRows : undefined,
          mask: inputMask,
        });
      }
    }
  }

  private getType(type: string) {
    switch (type) {
      case 'Строка':
        return ParameterType.String;
      case 'Целочисленное число':
        return ParameterType.Integer;
      case 'Число с плавающей точкой':
        return ParameterType.Decimal;
      case 'Время':
        return ParameterType.Time;
      case 'Дата':
        return ParameterType.Date;
      default:
        throw new Error(`Unexpected parameter type: ${type}`);
    }
  }
}
